// @flow
import * as React from 'react'
import gql from 'graphql-tag'
import { camelizeKeys } from 'humps'
import { ApolloProvider, Query } from 'react-apollo'

import ErrorBoundary from './ErrorBoundary'

import * as leadRepo from 'data/lead'
import { WhitelabelProvider } from 'main/framework'
import client from 'main/framework/apolloClient'
import { SSOContextProvider } from 'main/hooks/useSSOContext'
import { SendbirdContext, useSendbird } from 'main/util/sendbird'
import type { LeadSourceType, School, User } from 'types'
import { segment } from 'util/analytics'
import { withWebTranslation } from 'util/locize'
import { getReferrerData, setReferrerCookie } from 'util/referrer'

const OLD_BUNDLE_APP_QUERY = gql`
  query OldBundleAppQuery {
    currentNetwork {
      name
      whitelabelOptions
      availableLocales
      products
      origin
    }
  }
`

declare var ENV: any

type Props = {
  children: React.Node,
  i18n: any,
}

type State = {
  user: User | null,
  bookmarks: number[] | null,
}

export type AppContextValue = {
  user: User | null,
  bookmarks: number[] | null,
  setUser: (User | null) => void,
  addBookmark: (School, LeadSourceType | null) => void,
  removeBookmark: School => void,
}

export const AppContext = React.createContext({ user: null, bookmarks: null })

export class App extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    const user = window.lego.user ? camelizeKeys(window.lego.user) : null
    segment.identifyOrReset(user)

    this.state = {
      user,
      bookmarks: deriveBookmarksFromUser(user),
    }

    setReferrerCookie()
  }

  render = () => {
    const context: AppContextValue = {
      user: this.state.user,
      bookmarks: this.state.bookmarks,
      setUser: this.handleSetUser,
      addBookmark: this.handleAddBookmark,
      removeBookmark: this.handleRemoveBookmark,
    }

    // @ts-ignore
    const appId = window.SENDBIRD_APP_ID
    const userId = this.state.user && this.state.user.id
    const accessToken = this.state.user && this.state.user.sendbirdAccessToken

    return (
      <ApolloProvider client={client}>
        <AppContext.Provider value={context}>
          <Query query={OLD_BUNDLE_APP_QUERY} variables={{}}>
            {({ data, loading, error }) => {
              if (loading || error || !data) return null

              const enabledProducts = data?.currentNetwork?.products || []
              const whitelabelOptions = JSON.parse(data?.currentNetwork?.whitelabelOptions || '{}')
              const availableLocales = data?.currentNetwork?.availableLocales || [
                'en',
                'es',
                'ht',
                'vi',
              ]
              const networkName = data?.currentNetwork?.name
              const networkOrigin = data?.currentNetwork?.origin

              return (
                <WhitelabelProvider
                  enabledProducts={enabledProducts}
                  networkName={networkName}
                  whitelabelOptions={whitelabelOptions}
                  availableLocales={availableLocales}
                  networkOrigin={networkOrigin}
                >
                  <SendbirdWrapper appId={appId} userId={userId} accessToken={accessToken}>
                    <SSOContextProvider>
                      <ErrorBoundary>{this.props.children}</ErrorBoundary>
                    </SSOContextProvider>
                  </SendbirdWrapper>
                </WhitelabelProvider>
              )
            }}
          </Query>
        </AppContext.Provider>
      </ApolloProvider>
    )
  }

  componentDidMount() {
    const preferredLanguage = this.state.user && this.state.user.preferredLanguage

    if (this.props.i18n.changeLanguage) {
      this.props.i18n.changeLanguage(preferredLanguage)
    }
  }

  componentDidUpdate = (_prevProps: Props, prevState: State) => {
    const currentPreferredLanguage = this.state.user && this.state.user.preferredLanguage
    const prevPreferredLanguage = prevState.user && prevState.user.preferredLanguage

    // HACK (neftaly): Somehow the i18n instance doesn't get injected on some pages, for further investigation
    if (currentPreferredLanguage !== prevPreferredLanguage && this.props.i18n.changeLanguage) {
      this.props.i18n.changeLanguage(currentPreferredLanguage)
    }
  }

  handleSetUser = (user: User | null) => {
    this.setState({ user, bookmarks: deriveBookmarksFromUser(user) })

    segment.identifyOrReset(user)
  }

  handleAddBookmark = (school: School, sourceType: LeadSourceType | null) => {
    this.setState(prevState => ({ bookmarks: [...(prevState.bookmarks || []), school.id] }))

    segment.track('Bookmark', { schoolId: school.id })

    if (this.state.user) {
      createLead(school.id, sourceType)
    }
  }

  handleRemoveBookmark = (school: School) => {
    this.setState(prevState => ({
      bookmarks: (prevState.bookmarks || []).filter(x => x !== school.id),
    }))

    segment.track('Unbookmark', { schoolId: school.id })

    if (this.state.user) removeLead(school.id)
  }
}

function SendbirdWrapper(props) {
  const { appId, userId, accessToken, children } = props
  const sendBird = useSendbird(appId, userId, accessToken)

  return <SendbirdContext.Provider value={sendBird}>{children}</SendbirdContext.Provider>
}

function deriveBookmarksFromUser(user: User | null) {
  if (!user) return null

  const leads = user.leads || []

  return leads.filter(lead => lead.isBookmarked).map(lead => lead.schoolId)
}

function createLead(schoolId: number, sourceType: LeadSourceType | null) {
  const leadData = {
    sourceType: sourceType || 'bookmark',
    isBookmarked: true,
    referrer: getReferrerData(),
  }

  leadRepo.create(schoolId, leadData)
}

function removeLead(schoolId: number) {
  const leadData = { isBookmarked: false, referrer: getReferrerData() }
  leadRepo.create(schoolId, leadData)
}

export default withWebTranslation(App)
